// apps/widget/src/lib/preview.ts
//
// The dashboard's live preview: unsaved appearance settings arrive over
// postMessage and restyle this frame before the clinic presses Save.
//
// Deliberately narrow. It runs only on a ?preview=1 load, only accepts
// messages from the clinic's own dashboard origins, and only touches theme and
// copy — never the key, the mode or anything a patient's data flows through.

import { HEX_COLOR } from '@skinvizo/shared';
import type { WidgetConfig } from './api';

/** The dashboard's form, as the API stores it. Every field optional. */
export interface PreviewDraft {
  primaryColor?: string | null;
  accentColor?: string | null;
  buttonTextColor?: string | null;
  backgroundStyle?: string | null;
  radius?: number | null;
  font?: string | null;
  headline?: string | null;
  subtitle?: string | null;
  startLabel?: string | null;
  ctaLabel?: string | null;
  heroImageUrl?: string | null;
  showPoweredBy?: boolean;
  enabledMetrics?: string[];
}

export function isPreviewFrame(): boolean {
  return window.parent !== window && new URLSearchParams(location.search).get('preview') === '1';
}

/** The saved config with the draft laid over it, mirroring the public config's defaults. */
export function mergeDraft(base: WidgetConfig, draft: PreviewDraft): WidgetConfig {
  const color = (v: string | null | undefined): string | null => (v && HEX_COLOR.test(v) ? v : null);
  const text = (v: string | null | undefined, max: number): string | null => v?.trim().slice(0, max) || null;

  return {
    ...base,
    theme: {
      ...base.theme,
      primaryColor: color(draft.primaryColor) ?? base.theme.primaryColor,
      accentColor: color(draft.accentColor),
      buttonTextColor: color(draft.buttonTextColor),
      backgroundStyle: draft.backgroundStyle === 'tinted' ? 'tinted' : 'plain',
      radius: typeof draft.radius === 'number' ? Math.min(32, Math.max(0, Math.round(draft.radius))) : 12,
      font: text(draft.font, 60),
    },
    copy: {
      ...base.copy,
      headline: text(draft.headline, 160),
      subtitle: text(draft.subtitle, 240),
      startLabel: text(draft.startLabel, 40),
      ctaLabel: text(draft.ctaLabel, 40) ?? 'Book a consultation',
    },
    heroImageUrl: draft.heroImageUrl?.startsWith('https://') ? draft.heroImageUrl : null,
    showPoweredBy: draft.showPoweredBy ?? base.showPoweredBy,
    enabledMetrics: draft.enabledMetrics?.length ? draft.enabledMetrics : base.enabledMetrics,
  };
}

/**
 * Tells the dashboard the frame is ready for its draft, then applies each one
 * it sends. Returns the unsubscribe.
 */
export function listenForPreview(origins: string[], onDraft: (draft: PreviewDraft) => void): () => void {
  const allowed = origins.map((o) => o.toLowerCase());

  const onMessage = (e: MessageEvent): void => {
    if (e.source !== window.parent || !allowed.includes(e.origin.toLowerCase())) return;
    const data = e.data as { type?: string; draft?: PreviewDraft } | null;
    if (data?.type === 'gs:preview' && data.draft && typeof data.draft === 'object') onDraft(data.draft);
  };

  window.addEventListener('message', onMessage);
  // Addressed to each allowed origin in turn; the browser drops the ones that
  // do not match the parent, so nothing reaches a page that is not ours.
  for (const origin of allowed) window.parent.postMessage({ type: 'gs:preview:ready' }, origin);

  return () => window.removeEventListener('message', onMessage);
}
